import { useContext, useEffect, useMemo, useState } from 'react';

import { Formik } from 'formik';

import UserProfileFormFields from '../User/UserProfileFormFields';
import {
  buildProfileInitialValues,
  buildProfileValidationSchema,
  prepareProfilePayload,
} from '../User/profileFormShared';
import { EventContext } from '../../Contexts/EventContext';
import Loading from '../../Shared/Components/Loading';
import { error, success } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import { usePasswordPolicy } from '../../Shared/hooks/usePasswordPolicy';
import {
  getApiErrorMessage,
  getApiErrorStatus,
  serverErrorHandler,
} from '../../Shared/utils/helper';
import Master from '../Layouts/Master';

const EditProfile = () => {
  const eventContext = useContext(EventContext);
  const passwordPolicy = usePasswordPolicy();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [photoIds, setPhotoIds] = useState([]);
  const [profile, setProfile] = useState(null);

  const validationSchema = useMemo(
    () => buildProfileValidationSchema(passwordPolicy),
    [passwordPolicy],
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await useApi.get(
      '/province',
      {},
      (res) => {
        const provinces = [...res.data.data];
        const last = provinces?.pop();
        provinces?.unshift(last);
        eventContext.provinces = provinces;
      },
      () => error('خطا در دریافت استان‌ها'),
    );
    await useApi.get(
      '/user/profile',
      {},
      (res) => {
        setProfile(res.data.data);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        error(
          getApiErrorMessage(err, 'هنگام دریافت اطلاعات پروفایل خطایی رخ داده است'),
        );
      },
    );
  };

  const refreshAuthUser = async () => {
    await useApi.get(
      '/auth/me',
      {},
      (res) => {
        eventContext.authUser = res.data.data;
      },
      () => {},
    );
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const payload = prepareProfilePayload(values, photoIds);

    setSubmitLoading(true);
    await useApi.put(
      '/user/profile',
      payload,
      async (res) => {
        setProfile(res.data.data);
        await refreshAuthUser();
        success('پروفایل با موفقیت به‌روزرسانی شد');
        setSubmitLoading(false);
        setSubmitting(false);
      },
      (err) => {
        setSubmitLoading(false);
        setSubmitting(false);
        if (getApiErrorStatus(err) === 422) {
          serverErrorHandler(err?.data?.errors, () => {});
          error(getApiErrorMessage(err, 'اطلاعات وارد شده نامعتبر است'));
        } else {
          error('خطا در ذخیره پروفایل');
        }
      },
    );
  };

  if (loading || !profile) {
    return <Loading />;
  }

  return (
    <Master>
      <div className='d-flex flex-row justify-content-between border-bottom pb-2 my-3'>
        <div className='d-flex flex-row mt-2'>
          <i className='fad fa-user-edit f-20 text-secondary mx-2'></i>
          <h1 className='text-secondary f-13 font-weight-bold mt-1 mb-0 p-0'>
            ویرایش پروفایل
          </h1>
        </div>
      </div>
      <Formik
        enableReinitialize
        initialValues={buildProfileInitialValues(profile)}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <UserProfileFormFields
              values={values}
              errors={errors}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              provinces={eventContext.provinces}
              photoIds={photoIds}
              setPhotoIds={setPhotoIds}
            />
            <div className='col-12 d-flex justify-content-end my-3'>
              <button
                type='submit'
                className='btn btn-info'
                disabled={isSubmitting || submitLoading}
              >
                {submitLoading ? (
                  <span
                    className='spinner-border spinner-border-sm'
                    role='status'
                  />
                ) : (
                  'ذخیره تغییرات'
                )}
              </button>
            </div>
          </form>
        )}
      </Formik>
    </Master>
  );
};

export default EditProfile;
